<template>
  <div>
    <!-- Loading -->
    <div v-if="!loaded" class="text-center p-3">
      <b-spinner small />
    </div>

    <template v-if="loaded">

      <!-- ── Passkey section ─────────────────────────────────────────── -->
      <template v-if="$CONST('CONFIG').PASSKEYMFAENABLED === 'j'">
        <div class="mfa-section-title">
          <font-awesome-icon icon="fa-regular fa-key" class="mr-2" />
          Passkeys
        </div>

        <div v-if="!adminUserId" class="text-muted mb-3" style="font-size: 0.875rem">
          Dieser Benutzer ist kein ERP-Benutzer und hat keine Passkeys.
        </div>

        <template v-if="adminUserId">
          <div v-if="passkeys.length === 0" class="text-muted mb-3" style="font-size: 0.875rem">
            Keine Passkeys registriert.
          </div>
          <div
            v-for="pk in passkeys"
            :key="pk.id"
            class="d-flex align-items-center mb-2 p-2"
            style="border: 1px solid rgba(0,0,0,0.1); border-radius: 4px"
          >
            <font-awesome-icon icon="fa-regular fa-key" class="mr-3 text-muted" />
            <span style="font-size: 0.875rem">Hinzugefügt am {{ pk.createdAt }}</span>
          </div>

          <b-button
            variant="outline-warning"
            size="sm"
            class="mt-2"
            @click="confirmPasskeyReset"
            :disabled="passkeyBusy || passkeys.length === 0"
          >
            <font-awesome-icon icon="fa-solid fa-rotate-left" class="mr-1" />
            Alle Passkeys zurücksetzen
          </b-button>
          <small class="ml-2 text-muted">
            Der Benutzer muss sich beim nächsten Login neu registrieren.
          </small>

          <div v-if="passkeyError" class="alert alert-danger mt-2" style="font-size: 0.875rem">
            {{ passkeyError }}
          </div>
        </template>
      </template>

      <!-- ── TOTP section ────────────────────────────────────────────── -->
      <hr v-if="$CONST('CONFIG').PASSKEYMFAENABLED === 'j' && $CONST('CONFIG').AUTHENTICATORMFAENABLED === 'j'" class="my-4" />

      <template v-if="$CONST('CONFIG').AUTHENTICATORMFAENABLED === 'j'">
        <div class="mfa-section-title">
          <font-awesome-icon icon="fa-regular fa-mobile-screen" class="mr-2" />
          Authenticator-App (TOTP)
        </div>

        <div v-if="!adminUserId" class="text-muted mb-3" style="font-size: 0.875rem">
          Kein ERP-Benutzer — TOTP nicht verfügbar.
        </div>

        <template v-if="adminUserId">
          <div class="d-flex align-items-center mb-3" style="gap: 12px">
            <font-awesome-icon
              :icon="totpEnrolled ? 'fa-solid fa-circle-check' : 'fa-regular fa-circle-xmark'"
              :class="totpEnrolled ? 'text-success' : 'text-muted'"
            />
            <span style="font-size: 0.875rem">
              {{ totpEnrolled ? 'Authenticator-App eingerichtet' : 'Keine Authenticator-App eingerichtet' }}
            </span>
          </div>

          <b-button
            v-if="totpEnrolled"
            variant="outline-warning"
            size="sm"
            @click="confirmTotpReset"
            :disabled="totpBusy"
          >
            <font-awesome-icon icon="fa-solid fa-rotate-left" class="mr-1" />
            TOTP zurücksetzen
          </b-button>
          <small v-if="totpEnrolled" class="ml-2 text-muted">
            Der Benutzer muss beim nächsten Login eine neue Authenticator-App einrichten.
          </small>

          <div v-if="totpError" class="alert alert-danger mt-2" style="font-size: 0.875rem">
            {{ totpError }}
          </div>
        </template>
      </template>

    </template>
  </div>
</template>

<script>
import AppApi from "@/api/AppApi";

export default {
  name: "MfaReiter",
  props: {
    person: { type: String, required: true },
    shown:  { type: Boolean, default: false },
  },
  data() {
    return {
      loaded: false,
      adminUserId: null,
      passkeys: [],
      passkeyBusy: false,
      passkeyError: null,
      totpEnrolled: false,
      totpBusy: false,
      totpError: null,
    };
  },
  watch: {
    shown(val) {
      if (val && !this.loaded) this.load();
    },
  },
  mounted() {
    if (this.shown) this.load();
  },
  methods: {
    async load() {
      this.loaded = false;
      this.passkeyError = null;
      this.totpError = null;
      try {
        const passkeyEnabled = this.$CONST('CONFIG').PASSKEYMFAENABLED === 'j';
        const totpEnabled    = this.$CONST('CONFIG').AUTHENTICATORMFAENABLED === 'j';
        const requests = [];
        if (passkeyEnabled) requests.push(AppApi.get("passkey/admin", { params: { personId: this.person } }));
        if (totpEnabled)    requests.push(AppApi.get("totp/user"));
        const results = await Promise.all(requests);
        let i = 0;
        if (passkeyEnabled) {
          this.adminUserId = results[i].data.adminUserId || null;
          this.passkeys    = results[i].data.passkeys || [];
          i++;
        }
        if (totpEnabled) {
          this.totpEnrolled = results[i].data.enrolled === true;
        }
      } catch (e) {
        this.passkeyError = "Fehler beim Laden der MFA-Daten.";
      } finally {
        this.loaded = true;
      }
    },

    async confirmPasskeyReset() {
      if (!confirm("Alle Passkeys zurücksetzen? Der Benutzer muss sich beim nächsten Login neu registrieren.")) return;
      this.passkeyBusy = true;
      this.passkeyError = null;
      try {
        await AppApi.delete("passkey/admin", {
          params: { personId: this.person, reset: "true" },
        });
        await this.load();
      } catch (e) {
        this.passkeyError = e?.response?.data?.error || "Reset fehlgeschlagen.";
      } finally {
        this.passkeyBusy = false;
      }
    },

    async confirmTotpReset() {
      if (!confirm("TOTP zurücksetzen? Der Benutzer muss beim nächsten Login eine neue Authenticator-App einrichten.")) return;
      this.totpBusy = true;
      this.totpError = null;
      try {
        await AppApi.delete("totp/user");
        this.totpEnrolled = false;
      } catch (e) {
        this.totpError = e?.response?.data?.error || "TOTP-Reset fehlgeschlagen.";
      } finally {
        this.totpBusy = false;
      }
    },
  },
};
</script>

<style scoped>
.mfa-section-title {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 12px;
  opacity: 0.8;
}
</style>